/**
 * Color Picker Component
 * Color selector with presets, hex/rgb input, and opacity slider
 */

import React, { useState, useRef, useEffect } from 'react';

interface ColorPickerProps {
  value: string;
  onChange: (color: string) => void;
  label?: string;
  presets?: string[];
}

const DEFAULT_PRESETS = [
  '#000000', '#ffffff', '#f8fafc', '#f1f5f9', '#e2e8f0',
  '#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6',
  '#8b5cf6', '#ec4899', '#06b6d4', '#14b8a6', '#6366f1',
  'transparent',
];

export function ColorPicker({
  value,
  onChange,
  label,
  presets = DEFAULT_PRESETS,
}: ColorPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [hexInput, setHexInput] = useState(value || '');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setHexInput(value || '');
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleHexChange = (hex: string) => {
    setHexInput(hex);
    if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(hex) || hex === 'transparent') {
      onChange(hex);
    }
  };

  return (
    <div ref={ref} className="relative">
      {label && <label className="block text-xs text-gray-500 mb-1">{label}</label>}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 w-full border rounded px-2 py-1.5 text-sm hover:bg-gray-50"
      >
        <span
          className="w-5 h-5 rounded border border-gray-300 flex-shrink-0"
          style={{
            backgroundColor: value === 'transparent' ? undefined : value || '#ffffff',
            backgroundImage: value === 'transparent'
              ? 'linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%), linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%)'
              : undefined,
            backgroundSize: value === 'transparent' ? '8px 8px' : undefined,
            backgroundPosition: value === 'transparent' ? '0 0, 4px 4px' : undefined,
          }}
        />
        <span className="text-xs font-mono truncate">{value || 'none'}</span>
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 bg-white rounded-lg shadow-xl border p-3 w-56">
          {/* Native color input */}
          <div className="mb-2">
            <input
              type="color"
              value={value && value !== 'transparent' ? value : '#000000'}
              onChange={(e) => {
                onChange(e.target.value);
                setHexInput(e.target.value);
              }}
              className="w-full h-8 rounded border cursor-pointer"
            />
          </div>

          {/* Hex input */}
          <div className="mb-3">
            <input
              type="text"
              value={hexInput}
              onChange={(e) => handleHexChange(e.target.value)}
              placeholder="#000000"
              className="w-full border rounded px-2 py-1 text-xs font-mono"
            />
          </div>

          {/* Presets */}
          <div className="grid grid-cols-8 gap-1">
            {presets.map((color) => (
              <button
                key={color}
                type="button"
                onClick={() => {
                  onChange(color);
                  setHexInput(color);
                }}
                className={`w-5 h-5 rounded border transition-transform hover:scale-125 ${
                  value === color ? 'ring-2 ring-blue-500 ring-offset-1' : 'border-gray-300'
                }`}
                style={{
                  backgroundColor: color === 'transparent' ? undefined : color,
                  backgroundImage: color === 'transparent'
                    ? 'linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%), linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%)'
                    : undefined,
                  backgroundSize: color === 'transparent' ? '6px 6px' : undefined,
                  backgroundPosition: color === 'transparent' ? '0 0, 3px 3px' : undefined,
                }}
                title={color}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
